import { Button, Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, Input } from '@repo/ui'
import {
  Cable,
  Database,
  FileText,
  FolderOpen,
  Mail,
  MessageSquareText,
  Plus,
  Presentation,
  Search
} from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import type { ConversationShell } from '@/api/conversation'
import type { ConversationCollectionKind } from '@/features/conversation/ConversationShell'

type CommandResult = {
  key: string
  group: 'Actions' | 'Chats' | 'Workspaces' | 'Artifacts' | 'Collections'
  label: string
  description: string
  icon: React.ReactNode
  activate: () => void
}

export function ConversationCommandMenu({
  open,
  shell,
  selectedProjectId,
  onOpenChange,
  onOpenChat,
  onOpenWorkspace,
  onOpenCollection,
  onOpenArtifact,
  onStartChat
}: {
  open: boolean
  shell?: ConversationShell
  selectedProjectId?: number
  onOpenChange: (open: boolean) => void
  onOpenChat: (projectId: number, conversationId: number) => void
  onOpenWorkspace: (projectId: number) => void
  onOpenCollection: (kind: ConversationCollectionKind) => void
  onOpenArtifact: (projectId: number, kind: 'proposal' | 'presentation' | 'document', artifactId: number) => void
  onStartChat: (projectId: number) => void
}) {
  const [query, setQuery] = useState('')
  const searchRef = useRef<HTMLInputElement>(null)
  useEffect(() => {
    if (!open) return
    const frame = window.requestAnimationFrame(() => searchRef.current?.focus())
    return () => window.cancelAnimationFrame(frame)
  }, [open])
  const results = useMemo<Array<CommandResult>>(() => {
    if (shell === undefined) return []
    const items: Array<CommandResult> = []
    if (selectedProjectId !== undefined) {
      items.push({
        key: `new-chat:${selectedProjectId}`,
        group: 'Actions',
        label: 'New chat',
        description:
          shell.workspaces.find((workspace) => workspace.projectId === selectedProjectId)?.title ?? 'Workspace',
        icon: <Plus aria-hidden="true" />,
        activate: () => onStartChat(selectedProjectId)
      })
    }
    for (const workspace of shell.workspaces) {
      items.push({
        key: `workspace:${workspace.projectId}`,
        group: 'Workspaces',
        label: workspace.title,
        description: workspace.ownership === 'mine' ? 'Mine' : 'Shared workspace',
        icon: <FolderOpen aria-hidden="true" />,
        activate: () => onOpenWorkspace(workspace.projectId)
      })
      for (const chat of workspace.chats.items) {
        items.push({
          key: `chat:${workspace.projectId}:${chat.conversationId}`,
          group: 'Chats',
          label: chat.title,
          description: workspace.title,
          icon: <MessageSquareText aria-hidden="true" />,
          activate: () => onOpenChat(workspace.projectId, chat.conversationId)
        })
      }
    }
    const collections: Array<{
      kind: ConversationCollectionKind
      label: string
      icon: React.ReactNode
    }> = [
      { kind: 'proposals', label: 'Proposals', icon: <FileText aria-hidden="true" /> },
      { kind: 'presentations', label: 'Presentations', icon: <Presentation aria-hidden="true" /> },
      { kind: 'content', label: 'Content', icon: <Database aria-hidden="true" /> },
      { kind: 'connectors', label: 'Connectors', icon: <Cable aria-hidden="true" /> }
    ]
    if (selectedProjectId !== undefined) {
      const workspace = shell.workspaces.find((candidate) => candidate.projectId === selectedProjectId)
      for (const collection of collections) {
        const count = workspace?.counts[collection.kind]
        if (count?.available !== true) continue
        items.push({
          key: `collection:${collection.kind}`,
          group: 'Collections',
          label: collection.label,
          description: `${count.count} in ${workspace?.title ?? 'workspace'}`,
          icon: collection.icon,
          activate: () => onOpenCollection(collection.kind)
        })
      }
    }
    const expanded = shell.expanded
    if (expanded !== undefined) {
      // One list, so the kind comes off the row rather than from which loop we are in.
      for (const artifact of expanded.artifacts.items) {
        items.push({
          key: `${artifact.kind}:${expanded.projectId}:${artifact.id}`,
          group: 'Artifacts',
          label: artifact.title,
          description:
            artifact.kind === 'presentation' ? 'Presentation' : artifact.kind === 'document' ? 'Document' : 'Proposal',
          icon:
            artifact.kind === 'presentation' ? (
              <Presentation aria-hidden="true" />
            ) : artifact.kind === 'document' ? (
              <Mail aria-hidden="true" />
            ) : (
              <FileText aria-hidden="true" />
            ),
          activate: () => onOpenArtifact(expanded.projectId, artifact.kind, artifact.id)
        })
      }
    }
    return items
  }, [onStartChat, onOpenArtifact, onOpenChat, onOpenCollection, onOpenWorkspace, selectedProjectId, shell])
  const normalized = query.trim().toLocaleLowerCase()
  const filtered = results.filter(
    (result) =>
      normalized === '' ||
      `${result.label} ${result.description} ${result.group}`.toLocaleLowerCase().includes(normalized)
  )
  const grouped = ['Actions', 'Chats', 'Workspaces', 'Artifacts', 'Collections'] as const
  const activate = (result: CommandResult) => {
    setQuery('')
    onOpenChange(false)
    result.activate()
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setQuery('')
        onOpenChange(nextOpen)
      }}
    >
      {/* The shared DialogContent centres itself with top-[50%] translate-y-[-50%].
          Overriding only `top` leaves the translate in place, so the palette was
          centred on 18% of the viewport and its heading and search input rendered
          above the top edge. Cancel the translate and bound the height so the
          palette is top-anchored and always fully on screen. */}
      <DialogContent className="top-[18%] max-h-[78vh] translate-y-0 gap-0 overflow-hidden p-0 sm:max-w-xl">
        <DialogHeader className="sr-only">
          <DialogTitle>Search Snowvault</DialogTitle>
          <DialogDescription>Jump to a chat, workspace, artifact, collection, or action.</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-2 border-b px-4">
          <Search className="size-4 text-muted-foreground" aria-hidden="true" />
          <Input
            ref={searchRef}
            aria-label="Search Snowvault"
            value={query}
            className="h-12 border-0 px-0 shadow-none focus-visible:ring-0"
            placeholder="Search chats, workspaces, artifacts, and actions"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
        <div className="max-h-[min(28rem,60vh)] overflow-y-auto overscroll-contain p-2">
          {filtered.length === 0 && (
            <p className="px-3 py-8 text-center text-sm text-muted-foreground">No matching results.</p>
          )}
          {grouped.map((group) => {
            const groupResults = filtered.filter((result) => result.group === group)
            if (groupResults.length === 0) return null
            return (
              <section key={group} aria-labelledby={`command-group-${group}`} className="mb-2 last:mb-0">
                <h2
                  id={`command-group-${group}`}
                  className="px-2 py-1 text-[0.625rem] font-semibold tracking-[0.14em] text-muted-foreground uppercase"
                >
                  {group}
                </h2>
                <div className="space-y-1">
                  {groupResults.map((result) => (
                    <Button
                      key={result.key}
                      type="button"
                      variant="ghost"
                      className="h-auto w-full justify-start gap-3 px-2 py-2 text-left"
                      onClick={() => activate(result)}
                    >
                      <span className="text-muted-foreground [&_svg]:size-4">{result.icon}</span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm">{result.label}</span>
                        <span className="block truncate text-xs font-normal text-muted-foreground">
                          {result.description}
                        </span>
                      </span>
                    </Button>
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      </DialogContent>
    </Dialog>
  )
}
